import React from 'react';
import { connect } from 'react-redux';
import BaseComponent from '../../../../../baseClasses/BaseComponent';
import checkoffDefault from '../../../../../assets/images/checkoff-default.svg';
import checkoffNA from '../../../../../assets/images/checkoff-na.svg';
import checkoffChecked from '../../../../../assets/images/checkoff-checked.gif';
import StepApproval from '../../TaskCompletionSteps/StepApproval';
import CommentPopup from '../../TaskCompletionSteps/CommentPopup';
import TaskCompletionStepComments from '../TaskCompletionStepComments';
import AnalyticsLogger from '../../../../../helpers/analytics/analyticsLogger';
import dataModel from '../../../../../models/DataModel';
import DataSource from '../../../../../dao/DataSource/DataSource';

import './Checkoff.scss';

const completionCiteriaSelector = {
  completion_criteria: {
    attachments: {
      additionalFields: ['upload', 'photo_longitude', 'photo_latitude', 'photo_taken_at'],
      conversions: {},
      user: {},
    },
    comments: { user: { additionalFields: ['profile_image'] } },
    event_histories: { activity: {}, activity_sub_type: {}, user_who_performed_action: {} },
    form_fields: {},
  },
};

export class Checkoff extends BaseComponent {
  constructor(props) {
    super(props);
    this.state = {
      completionStep: props.completionStep,
    };
    const currentRole = props.currentUser ? props.currentUser.role : null;
    this.analyticsLogger = new AnalyticsLogger({
      currentRole,
      isMobile: !!window.cordova,
    });
  }

  handleCSUpdate(completionStep, action, acData, fetchedData) {
    this.setState({ completionStep }); 
  }

  componentDidMount() {
    this.csDataSource = new DataSource('CompletionCriterion', this.props.completionStep.id, completionCiteriaSelector, this.handleCSUpdate);
    this.csDataSource.get();
  }

  componentWillUnmount() {
    if (this.csDataSource) {
      this.csDataSource.release();
      this.csDataSource = null;
    }
  }

  componentDidUpdate(prevProps) {
  }

  handleCompleteCS(completed) {
    const { permissions, userStepPermissions } = this.props;
    const { completionStep } = this.state;

    if (permissions.canCompleteTask && (userStepPermissions.has('step_complete') || userStepPermissions.has('unmarkNA') ||
      userStepPermissions.has('override'))) {
      this.csDataSource.performAction('completeCompletionCriterion', completionStep, completed);
    }
  }

  handleStepToggle(e) {
    const checkedState = e.target.name;
    const { userStepPermissions } = this.props;
    if (checkedState === 'checked' && (userStepPermissions.has('override') || userStepPermissions.has('unmarkNA'))) {
      this.handleUncheckPopup();
    }
    else if (checkedState === 'na' && (userStepPermissions.has('override') || userStepPermissions.has('unmarkNA'))) {
      this.handleUncheckPopup();
    }
    else if (checkedState === 'unchecked' && userStepPermissions.has('step_complete')) {
      this.handleCompleteCS(true);
    }
  }

  handleUncheckPopup() {
    const stepName = this.state.completionStep.name
      .split(' ')
      .map(
        word => word.substr(0, 1).toUpperCase() + word.substr(1).toLowerCase()
      )
      .join(' ');

    const message = (
      <div>
        <p>Do you want to uncheck the completion step {stepName}?</p>
      </div>
    );

    dataModel.showGlobalModal('SYSTEM_POPUP', {
      height: 184,
      width: 320,
      message: message,
      cancelButtonText: 'Cancel',
      okButtonText: 'Yes',
      handleOwnerOk: () => {
        this.confirmUncheck();
      },
      handleOwnerCancel: () => {
        this.cancelUncheck();
      },
    });
  };

  confirmUncheck = () => {
    dataModel.systemPopup = null;
    this.handleCompleteCS(false);
  };

  cancelUncheck = () => {
    dataModel.systemPopup = null;
  };

  handleStepApprove() {
    const { userStepPermissions } = this.props;
    const { completionStep } = this.state;
    if (userStepPermissions.has('is_approver')) {
      this.handleApproved(completionStep);
    }
    else if (userStepPermissions.has('is_reviewer')) {
      this.handleReviewed(completionStep);
    }
  }

  handleStepReject() {
    const { userStepPermissions, task } = this.props;
    if (userStepPermissions.has('accept/reject')) {
      if((task.has_reviewer && this.state.completionStep.reviewed_at) || !task.has_reviewer){
        this.handleRejectApproval(this.state.completionStep);
      }
      else{
        this.handleRejectReview(this.state.completionStep);
      }
    }
  }

  handleReviewed(completionStep) {
    this.csDataSource.performAction('reviewCompletionCriterion', completionStep);
  }

  handleRejectReview(completionStep) {
    this.setState({ rejectionComment: true });
  }

  handleApproved(completionStep) {
    this.csDataSource.performAction('approveCompletionCriterion', completionStep);
  }

  handleRejectApproval(completionStep) {
    this.setState({ rejectionComment: true });
  }

  handleCancelComment() {
    this.setState({
      rejectionComment: false,
    });
  }

  handleCommentSent(type, commentText) {
    this.setState({ rejectionComment: false });
    
    const { completionStep } = this.state;
    this.csDataSource.performAction('rejectCompletionCriterion', completionStep, commentText);
  }

  handleCommentChange(e) {
    this.setState({ commentText: e.target.value });
  }
  
  handleSetInputElement(element) {
    if (element) {
      this.inputElement = element;
      this.inputElement.focus();
    }  
  }

  handleDetermineRejected(task){
    const stepsRejected = task.completion_criteria
    ? task.completion_criteria.filter(cc => {
      if (cc) {
        return (
          cc.rejected_at &&
          !cc.approved_at &&
          !cc.reviewed_at &&
          !cc.completed_at
        );
      }
      else {
        return false;
      }
    }
    )
    : [];
    return stepsRejected.length > 0;
  }

  // Marking a step N/A completes it, so it needs 'step_complete'. Reviewers and
  // approvers never get that permission - they only review, reject and comment.
  canMarkNotApplicable() {
    const { userStepPermissions } = this.props;
    const { completionStep } = this.state;
    return !completionStep.not_applicable && userStepPermissions.has('step_complete');
  }

  handleNotApplicableButton() {
    if (!this.canMarkNotApplicable()) {
      return;
    }
    const { completionStep } = this.state;

    //check if a response exists

    if (completionStep.response) {
      //display popup
      dataModel.showGlobalModal('SYSTEM_POPUP', {
        height: 184,
        width: 320,
        message: this.textMap.markPartialFormAsNA,
        cancelButtonText: 'Cancel',
        okButtonText: 'Yes',
        handleOwnerOk: () => {
          this.handleNotApplicableSend();
        },
        handleOwnerCancel: () => {},
      });
    }
    else {
      this.handleNotApplicableSend();
    }

  }

  handleNotApplicableSend() {
    const { completionStep } = this.state;
    this.csDataSource.performAction('markNotApplicable', completionStep);
  }

  // Undoing N/A writes to the step, so it needs one of the step-editing
  // permissions. The clauses used to be written without grouping parentheses,
  // so `&&` bound tighter than `||` and the trailing
  // `!reviewed_at && !approved_at` case escaped both the not_applicable check
  // and the permission check. That let a reviewer on an Under Review task see
  // the N/A button on checkoff steps - reviewers only get 'accept/reject' and
  // 'add_comment', never 'step_complete'/'step_update'/'unmarkNA'. Matches the
  // grouping already used by PhotoUpload, MiscUpload and FormUpload.
  canUndoNotApplicable() {
    const { userStepPermissions } = this.props;
    const { completionStep } = this.state;

    if (!completionStep.not_applicable) {
      return false;
    }
    if (!(userStepPermissions.has('step_complete') || userStepPermissions.has('step_update'))) {
      return false;
    }
    const reviewed = !!completionStep.reviewed_at;
    const approved = !!completionStep.approved_at;

    return (reviewed && !approved) ||
      (!reviewed && approved) ||
      (!reviewed && !approved &&
        (completionStep.status !== 'completed' || userStepPermissions.has('unmarkNA')));
  }

  handleUndoNotApplicable() {
    if (!this.canUndoNotApplicable()) {
      return;
    }
    const { completionStep } = this.state;
    this.csDataSource.performAction('unmarkNotApplicable', completionStep);
  }

  createButtonList() {
    const { userStepPermissions, task } = this.props;
    const { completionStep } = this.state;
    
    let buttons = [];

    if (task.status !== 'completed' && 
      !(completionStep.status === 'complete' || completionStep.status === 'completed')) {
      if (this.canMarkNotApplicable()) {
        buttons.push(
          <div
            className="button na-button"
            key="no-image-button"
            onClick={this.handleNotApplicableButton}
            data-qa="not-applicable-button"
          />
        );
      }
      else if (this.canUndoNotApplicable()) {
        buttons.push(
          <div
            className="button undo-na-button"
            key="undo-na-button"
            onClick={this.handleUndoNotApplicable}
            data-qa="not-applicable-button"
          />
        );
      }
      return <div
        className={`button-list ${
          userStepPermissions.has('accept/reject')
            ? 'review-approve'
            : ''}
      `}
      >
        {buttons}
      </div>;
    }
    else {
      return null;
    }

  }
  
  render() {
    const { 
      editing, 
      task, 
      userStepPermissions, 
    } = this.props;
    const { completionStep } = this.state;
    if (!completionStep) {
      return null;
    }
    let toggleImage;
    let imgClassName = 'checkoff';

    let buttons = this.createButtonList();   
    const completionStepStatus = this.state.completionStep.status; 
    
    if (completionStep.not_applicable) {
      imgClassName += '-checked';
      if (userStepPermissions.has('override') || userStepPermissions.has('unmarkNA')) {
        imgClassName += ' checkoff-checked--clickable';
      }
      toggleImage = (
        <img
          name="na"
          src={checkoffNA}
          onClick={this.handleStepToggle}
          className={imgClassName}
          alt="Completion step NA"
        />
      );
    } else if (completionStepStatus === 'completed' || 
              completionStepStatus === 'pending_review' ||
              completionStepStatus === 'pending_approval' ||
              completionStepStatus === 'approved' ||
              completionStepStatus === 'rejected') {
      imgClassName += '-checked';
      if (userStepPermissions.has('override')) {
        imgClassName += ' checkoff-checked--clickable';
      }
      toggleImage = (
        <img
          name="checked"
          src={checkoffChecked}
          onClick={this.handleStepToggle}
          className={imgClassName}
          alt="Completion step checked"
        />
      );
    } else if (completionStepStatus === 'not_started') {
      imgClassName += '-unchecked';
      if (userStepPermissions.has('step_complete')) {
        imgClassName += ' checkoff-unchecked--clickable';
      }
      toggleImage = (
        <img
          name="unchecked"
          src={checkoffDefault}
          onClick={!this.props.projectTemplate ? this.handleStepToggle : undefined}
          className={imgClassName}
          alt="Completion step unchecked"
        />
      );
    }

    let background, commentDisplay, reviewApproveButtons, comments;
    
    if (!editing) {
      if (this.state.rejectionComment) {
        const element = document.getElementById(
          'task-completion-step-comments-' + completionStep.id
        );
        const rect = element.getBoundingClientRect();
        commentDisplay = (
          <CommentPopup
            task={task}
            completionStep={completionStep}
            height={element.clientHeight}
            width={element.clientWidth}
            left={rect.left}
            top={rect.top - 20}
            type={'REJECTED'}
            commentSent={commentText =>
              this.handleCommentSent('reject', commentText)
            }
            placeholder={
              'You must leave a comment to reject a Completion Step.'
            }
            cancelComment={this.handleCancelComment}
          />
        );
      }
  
      if (!this.state.rejectionComment && !completionStep.rejected_at && !editing &&
        userStepPermissions.has('accept/reject')) {
        reviewApproveButtons = (
          <div className="approve-reject-buttons">
          <StepApproval key={(task.has_reviewer && (userStepPermissions.has('is_reviewer') || this.props.currentUser.super_admin) &&
            !completionStep.reviewed_at ? '_review' : '_approve') + completionStep.id} userStepPermissions={userStepPermissions}
              handleStepReject={this.handleStepReject}
              handleStepApprove={this.handleStepApprove}
            />
          </div>
        );
      }
  
      comments = (
        <TaskCompletionStepComments
          id={'task-completion-step-comments-' + this.props.completionStep.id}
          task={this.props.task}
          completionStep={this.props.completionStep}
        />
      );
    }
    if (this.props.projectTemplate){
      commentDisplay = null;
      comments = null;
      reviewApproveButtons = null;
      buttons = null;
    }
    return (
      <div className="task-completion-step checkoff">
        {background}
        {this.props.completionStep.instructions && (
          <div className="checkoff-instructions">
            <div className="instruction-label">Instructions</div>
            <div className="instruction-text">
              {this.props.completionStep.instructions}
            </div>
        </div>)}
        <div className="button-container">
          {toggleImage}
          {buttons}
        </div>
        {reviewApproveButtons}
        {comments}
        {commentDisplay}
      </div>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  return {
    currentUser: state.new_users.currentUser,
    permissions: state.new_users.permissions,
  };
};

// For handling state change from a dispatch
export default connect(mapStateToProps, null)(Checkoff);



// WEBPACK FOOTER //
// ./src/components/TaskCRUD/TaskCompletionStepsContainer/TaskCompletionSteps/Checkoff/index.js